/**
 * Barber Boss header enhancements.
 * - Hover-intent + Escape + touch handling for the mega menus (CSS :hover/:focus-within already work without JS).
 * - Cart count sync after Horizon cart updates (StandardEvents.cartLinesUpdate), with a /cart.js fallback.
 * - Prevents anchor navigation when the target Horizon dialog (cart drawer / search modal) exists.
 * - Publishes --header-height / --header-group-height for Horizon consumers that expect them.
 * - Sticky header that hides on scroll down and reappears on scroll up.
 */

const OPEN_DELAY = 90;
const CLOSE_DELAY = 160;
const DESKTOP_QUERY = '(min-width: 900px)';

function initHeader(root) {
  root.classList.add('bb-header--js');

  const bar = root.querySelector('[data-bb-header-bar]');
  const items = Array.from(root.querySelectorAll('[data-bb-nav-item]'));
  const desktop = window.matchMedia(DESKTOP_QUERY);
  const coarse = window.matchMedia('(pointer: coarse)');
  const closeMobile = initMobileDrawer(root);

  const setOpen = (item, open) => {
    item.classList.toggle('is-open', open);
    const trigger = item.querySelector('[data-bb-nav-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const closeAll = (except) => {
    items.forEach((item) => {
      if (item !== except) setOpen(item, false);
    });
  };

  items.forEach((item) => {
    let openTimer = 0;
    let closeTimer = 0;
    const trigger = item.querySelector('[data-bb-nav-trigger]');

    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      window.clearTimeout(closeTimer);
      openTimer = window.setTimeout(() => {
        closeAll(item);
        setOpen(item, true);
      }, OPEN_DELAY);
    });

    item.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      window.clearTimeout(openTimer);
      closeTimer = window.setTimeout(() => setOpen(item, false), CLOSE_DELAY);
    });

    // Touch on desktop widths: first tap opens the panel, second tap follows the link.
    trigger?.addEventListener('click', (event) => {
      if (!coarse.matches || !desktop.matches) return;
      if (item.classList.contains('is-open')) return;
      event.preventDefault();
      closeAll(item);
      setOpen(item, true);
    });
  });

  bar?.addEventListener('pointerleave', () => closeAll());

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Node) || bar?.contains(event.target)) return;
    closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const active = document.activeElement;
    const insideNav = items.some((item) => item.contains(active));
    closeAll();
    if (insideNav && active instanceof HTMLElement) active.blur();
  });

  desktop.addEventListener('change', (event) => {
    if (event.matches) closeMobile({ restoreFocus: false, instant: true });
    if (!event.matches) closeAll();
  });

  // Anchors that double as Horizon dialog triggers: keep href as no-JS fallback only.
  root.querySelectorAll('[data-bb-dialog-link]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const id = link.getAttribute('data-bb-dialog-link');
      if (id && document.getElementById(id)) event.preventDefault();
    });
  });

  // The account popover is positioned from the top of the viewport; the bar's
  // bottom edge moves with the sticky header, so measure it when it opens.
  const account = root.querySelector('[data-bb-account]');
  const placeAccountPopover = () => {
    const stick = root.querySelector('[data-bb-header-stick]');
    if (!account || !stick) return;
    account.style.setProperty('--bb-account-top', `${Math.round(stick.getBoundingClientRect().bottom) + 10}px`);
  };
  account?.addEventListener('pointerdown', placeAccountPopover);
  account?.addEventListener('keydown', placeAccountPopover);

  publishHeights(root);
  initScrollReveal(root);
}

const REVEAL_DELTA = 8;
let revealAbort = null;

/**
 * Hides the sticky header group while scrolling down and reveals it on scroll up.
 * Horizon scrolls inside `.page-wrapper` (html/body are overflow: hidden), so both
 * that element and window are watched and the larger offset wins.
 */
function initScrollReveal(root) {
  revealAbort?.abort();
  const group = document.getElementById('header-group');
  if (!group) return;
  group.classList.remove('bb-header-group--hidden', 'bb-header-group--pinned');
  if (!root.classList.contains('bb-header--sticky')) return;

  revealAbort = new AbortController();
  const { signal } = revealAbort;
  const reveal = root.hasAttribute('data-bb-header-reveal');
  const scroller = document.querySelector('.page-wrapper');
  const getY = () => Math.max(window.scrollY, scroller?.scrollTop ?? 0);

  let lastY = getY();
  let hidden = false;

  const setHidden = (value) => {
    if (hidden === value) return;
    hidden = value;
    group.classList.toggle('bb-header-group--hidden', value);
    document.body.style.setProperty(
      '--sticky-header-offset',
      value ? '0px' : `${root.querySelector('[data-bb-header-stick]')?.offsetHeight ?? 0}px`
    );
  };

  // Keep the header visible while the user is interacting with it.
  const isBusy = () =>
    document.documentElement.classList.contains('bb-scroll-lock') ||
    document.documentElement.hasAttribute('scroll-lock') ||
    root.querySelector('[data-bb-nav-item].is-open, :focus-visible') !== null;

  const onScroll = () => {
    const y = getY();
    const delta = y - lastY;
    const pastHeader = y > group.offsetHeight;
    group.classList.toggle('bb-header-group--pinned', y > 0);

    if (!reveal || !pastHeader) {
      setHidden(false);
      lastY = y;
      return;
    }
    if (Math.abs(delta) < REVEAL_DELTA) return;
    if (delta > 0 && !isBusy()) setHidden(true);
    else if (delta < 0) setHidden(false);
    lastY = y;
  };

  window.addEventListener('scroll', onScroll, { passive: true, signal });
  scroller?.addEventListener('scroll', onScroll, { passive: true, signal });
  // Other modules (fly-to-cart) need the cart visible.
  document.addEventListener('bb:header:reveal', () => setHidden(false), { signal });

  // Keyboard users tabbing into a hidden header bring it back.
  root.addEventListener(
    'focusin',
    (event) => {
      if (event.target instanceof Element && event.target.matches(':focus-visible')) setHidden(false);
    },
    { signal }
  );
  setHidden(false);
  onScroll();
}

/**
 * Mobile drawer built on a modal <dialog>: showModal() puts it in the top layer, so it is
 * never clipped by the header's backdrop-filter or trapped under a stacking context.
 * Returns a close function; a no-op when the markup is missing.
 */
function initMobileDrawer(root) {
  const toggle = root.querySelector('[data-bb-mobile-toggle]');
  const dialog = root.querySelector('[data-bb-mobile-panel]');
  const closeButton = root.querySelector('[data-bb-mobile-close]');
  if (!toggle || !(dialog instanceof HTMLElement)) return () => {};

  const supportsModal = typeof dialog.showModal === 'function';
  // Legacy browsers (no <dialog>): lift the panel out of the filtered header bar instead.
  if (!supportsModal) document.body.appendChild(dialog);
  let closeTimer = 0;
  let restoreFocusOnClose = true;

  const setLocked = (locked) => {
    document.documentElement.classList.toggle('bb-scroll-lock', locked);
    toggle.setAttribute('aria-expanded', locked ? 'true' : 'false');
  };

  const isOpen = () => dialog.classList.contains('is-open');

  const finishClose = () => {
    window.clearTimeout(closeTimer);
    dialog.removeEventListener('transitionend', onTransitionEnd);
    if (supportsModal && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    setLocked(false);
    if (restoreFocusOnClose) toggle.focus({ preventScroll: true });
  };

  function onTransitionEnd(event) {
    if (event.target === dialog && event.propertyName === 'transform') finishClose();
  }

  const open = () => {
    window.clearTimeout(closeTimer);
    dialog.removeEventListener('transitionend', onTransitionEnd);
    if (!dialog.open) {
      if (supportsModal) dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    // Commit the closed transform before adding the class so the slide-in animates.
    void dialog.offsetWidth;
    dialog.classList.add('is-open');
    setLocked(true);
    closeButton?.focus({ preventScroll: true });
  };

  const close = ({ restoreFocus = true, instant = false } = {}) => {
    if (!isOpen()) return;
    restoreFocusOnClose = restoreFocus;
    dialog.classList.remove('is-open');

    const duration = parseFloat(getComputedStyle(dialog).transitionDuration) || 0;
    if (instant || duration === 0) {
      finishClose();
      return;
    }
    dialog.addEventListener('transitionend', onTransitionEnd);
    // Fallback in case transitionend never fires (tab hidden, interrupted transition).
    closeTimer = window.setTimeout(finishClose, duration * 1000 + 150);
  };

  toggle.addEventListener('click', () => (isOpen() ? close() : open()));
  closeButton?.addEventListener('click', () => close());

  // Clicks on the ::backdrop are dispatched to the dialog itself.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  // Escape: animate out instead of the native instant close.
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  // Keep state consistent if something else closes the dialog.
  dialog.addEventListener('close', () => {
    if (!isOpen()) return;
    dialog.classList.remove('is-open');
    setLocked(false);
  });

  // Any link inside closes the drawer (same-page anchors, search modal, navigation).
  dialog.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link) close({ restoreFocus: false });
  });

  // Returning through the back/forward cache must not show a stale open drawer.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) close({ restoreFocus: false, instant: true });
  });

  return close;
}

function publishHeights(root) {
  const group = document.getElementById('header-group');
  const bar = root.querySelector('[data-bb-header-bar]');
  if (!bar) return;

  const announce = root.querySelector('.bb-announce');

  const update = () => {
    document.body.style.setProperty('--header-height', `${bar.offsetHeight}px`);
    if (group) {
      document.body.style.setProperty('--header-group-height', `${group.offsetHeight}px`);
      // Lets the sticky group's negative top scroll the announcement away.
      group.style.setProperty('--bb-announce-h', `${announce?.offsetHeight ?? 0}px`);
    }
  };

  update();
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    if (group) observer.observe(group);
  }
}

function setCartCount(count) {
  if (typeof count !== 'number' || Number.isNaN(count)) return;
  document.querySelectorAll('[data-bb-cart-count]').forEach((el) => {
    el.textContent = String(count);
    if (el.hasAttribute('data-bb-cart-badge')) el.classList.toggle('is-zero', count === 0);
  });
}

async function refetchCartCount() {
  try {
    const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    const cart = await response.json();
    setCartCount(cart.item_count);
  } catch (_) {
    // no-op: the server-rendered count stays in place
  }
}

async function initCartSync() {
  let eventName = 'cart:update';
  try {
    const { StandardEvents } = await import('@shopify/events');
    if (StandardEvents?.cartLinesUpdate) eventName = StandardEvents.cartLinesUpdate;
  } catch (_) {
    // importmap missing: fall back to refetching on the generic event name
  }

  document.addEventListener(eventName, (event) => {
    const promise = event.promise;
    if (promise && typeof promise.then === 'function') {
      promise
        .then(({ cart, detail }) => {
          const count = cart?.totalQuantity ?? detail?.itemCount;
          if (typeof count === 'number') setCartCount(count);
          else refetchCartCount();
        })
        .catch(() => refetchCartCount());
    } else {
      refetchCartCount();
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refetchCartCount();
  });
}

document.querySelectorAll('[data-bb-header]').forEach(initHeader);
initCartSync();

// Re-init when the theme editor re-renders the section.
document.addEventListener('shopify:section:load', (event) => {
  const root = event.target?.querySelector?.('[data-bb-header]');
  if (root) initHeader(root);
});
